/**
 * The sign-in screen's brand surface, and the only file in this console that
 * carries raw colour outside `platform/palette.ts`.
 *
 * Rule 6 says the toolkit's palette is the only sanctioned source of hex. This
 * is the sanctioned exception, in the same class as the canvas palette and the
 * favicon grey, and it exists for the same kind of reason: these are not theme
 * colours. They are one brand surface, identical across the NOC, the Portal
 * and both consoles, and a surface that must look the same everywhere cannot
 * be derived from a theme that differs everywhere.
 *
 * Copied from the NOC's `--login-*` block, which is where this recipe is
 * defined (`Wall-E-Eco-Tap-Gerard-tool/v2/frontend/src/platform/theme.css`),
 * and recorded as section 11 of the console design package. Values change
 * there first, here second, never the other way round.
 *
 * The screen is also the sanctioned exception to the flat aesthetic. Gradients
 * belong to a brand surface and nowhere else in the app.
 */

/** The stage behind the card. */
export const LOGIN_BACKDROP = '#0b1f3a'
export const LOGIN_BACKDROP_MID = '#1b4585'
export const LOGIN_BACKDROP_DEEP = '#07142a'

/** The brand panel, left of the split. */
export const LOGIN_PANEL_1 = '#0e2c5a'
export const LOGIN_PANEL_2 = '#1c54a6'
export const LOGIN_PANEL_3 = '#4f8fe0'

/** The Microsoft mark, four squares, in Microsoft's own colours. */
export const MS_RED = '#f25022'
export const MS_GREEN = '#7fba00'
export const MS_BLUE = '#00a4ef'
export const MS_YELLOW = '#ffb900'

/**
 * The primary button. Two values because the recipe names two: the surface it
 * sits on is a themed card, not the brand panel, so this one follows the
 * theme's base rather than staying fixed like everything above.
 */
export const LOGIN_PRIMARY = { light: '#0f6cbd', dark: '#2886de' } as const

export const LOGIN_BACKDROP_IMAGE =
  `radial-gradient(120% 120% at 18% 12%, ${LOGIN_BACKDROP_MID} 0%, ` +
  `${LOGIN_BACKDROP} 48%, ${LOGIN_BACKDROP_DEEP} 100%)`

export const LOGIN_PANEL_GRADIENT =
  `linear-gradient(150deg, ${LOGIN_PANEL_1} 0%, ${LOGIN_PANEL_2} 55%, ${LOGIN_PANEL_3} 100%)`

/** The soft light from the lower right of the brand panel. */
export const LOGIN_PANEL_HIGHLIGHT =
  'radial-gradient(90% 70% at 80% 100%, rgba(255,255,255,.16) 0%, transparent 55%)'

/** White, and the translucencies drawn over the brand panel. Not theme
 *  colours: this panel is the same blue in every theme, so what sits on it is
 *  the same white in every theme. */
export const ON_BRAND = '#ffffff'
export const ON_BRAND_SUBTLE = 'rgba(255,255,255,.85)'
export const ON_BRAND_FAINT = 'rgba(255,255,255,.6)'
export const ON_BRAND_RULE = 'rgba(255,255,255,.4)'
export const CARD_HAIRLINE = 'rgba(255,255,255,.10)'
export const CARD_SHADOW = '0 30px 80px rgba(0,0,0,.45)'
export const TOGGLE_FILL = 'rgba(255,255,255,.08)'
export const TOGGLE_STROKE = 'rgba(255,255,255,.18)'
